import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';
import { isWebAuthnSupported } from '@api/passkeyApi';
import {
  authenticateWithConditionalPasskey,
  isConditionalPasskeyAvailable,
} from '@utils/passkeyUtils';

type UseConditionalPasskeyOptions = {
  enabled?: boolean;
  onSuccess?: (result: { success?: boolean; data?: unknown }) => void;
  onError?: (message: string) => void;
};

/**
 * On supported browsers, primes passkey autofill in the email field (conditional UI).
 */
export function useConditionalPasskey({
  enabled = true,
  onSuccess,
  onError,
}: UseConditionalPasskeyOptions) {
  const primedRef = useRef(false);
  const handledRef = useRef(false);

  useEffect(() => {
    if (
      !enabled ||
      Platform.OS !== 'web' ||
      !isWebAuthnSupported() ||
      primedRef.current
    ) {
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const available = await isConditionalPasskeyAvailable();
        if (!available || cancelled) return;

        primedRef.current = true;
        const result = await authenticateWithConditionalPasskey();
        if (!result || cancelled || handledRef.current) return;

        handledRef.current = true;
        onSuccess?.(result);
      } catch (error: any) {
        if (!cancelled) {
          onError?.(error?.message || 'Passkey sign-in failed');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, onError, onSuccess]);
}
